const TRACCAR_TIMEOUT = 60000; // 60s for slow Athuraya server

/**
 * Robustly parse a GPS provider time value into epoch milliseconds (UTC).
 *
 * Providers (GPSWOX / Traccar) report time in several shapes:
 *  - Unix epoch in seconds OR milliseconds
 *  - ISO 8601 with timezone ("...Z" or "...+01:00")
 *  - "YYYY-MM-DD HH:MM:SS" (or with "T") WITHOUT a timezone — these are UTC by
 *    API convention. JS's Date() would wrongly treat them as browser-local,
 *    which inflates computed durations by the local UTC offset (the bug).
 *
 * @param {string|number|null} value
 * @returns {number|null} epoch ms, or null if unparseable
 */
export const parseGpsTime = (value) => {
  if (value === null || value === undefined || value === '') return null;

  // Numeric epoch (seconds vs milliseconds)
  if (typeof value === 'number') {
    return value < 1e12 ? value * 1000 : value;
  }
  const s = String(value).trim();
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    return n < 1e12 ? n * 1000 : n;
  }

  // Already carries an explicit timezone (Z or ±HH:MM / ±HHMM) → trust it.
  if (/(z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    const t = Date.parse(s);
    return isNaN(t) ? null : t;
  }

  // No timezone → treat as UTC (provider convention).
  const t = Date.parse(s.replace(' ', 'T') + 'Z');
  if (!isNaN(t)) return t;

  const fallback = Date.parse(s);
  return isNaN(fallback) ? null : fallback;
};

const SERVER_PROXY = (() => {
  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    if (!protocol.startsWith('file')) {
      return `${protocol}//${host}/proxy/`;
    }
  }
  return null;
})();

const CORS_PROXIES = [
  ...(SERVER_PROXY ? [SERVER_PROXY] : []),
  'https://api.allorigins.win/raw?url=',
  'https://api.codetabs.com/v1/proxy?quest=',
  'https://thingproxy.freeboard.io/fetch/',
  'https://yacdn.org/proxy/'
];

export const fetchTraccar = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TRACCAR_TIMEOUT);
  
  // First: try direct fetch
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') throw new Error('TIMEOUT');
    
    // CORS error → try proxy fallback
    if (err.message === 'Load failed' || err.message === 'Failed to fetch' || err.name === 'TypeError') {
      const proxies = [];
      try {
        const saved = localStorage.getItem('appSettings');
        if (saved) {
          const settings = JSON.parse(saved);
          if (settings.corsProxy) proxies.push(settings.corsProxy);
        }
      } catch {}
      try {
        const cached = sessionStorage.getItem('gpswox_working_proxy');
        if (cached && !proxies.includes(cached)) proxies.push(cached);
      } catch {}
      
      CORS_PROXIES.forEach(p => { if (!proxies.includes(p)) proxies.push(p); });
      // Always try OUR OWN server proxy FIRST — it's trusted and reaches GPS
      // servers (incl. bad-SSL ones) reliably.
      if (SERVER_PROXY) {
        const i = proxies.indexOf(SERVER_PROXY);
        if (i > 0) { proxies.splice(i, 1); proxies.unshift(SERVER_PROXY); }
        else if (i < 0) { proxies.unshift(SERVER_PROXY); }
      }

      const isPost = options.method === 'POST';

      for (const proxy of proxies) {
        const isOwnProxy = SERVER_PROXY && proxy === SERVER_PROXY;
        try {
          const proxyController = new AbortController();
          const proxyTimeout = setTimeout(() => proxyController.abort(), TRACCAR_TIMEOUT);

          const proxiedUrl = proxy.includes('?url=') || proxy.includes('?quest=')
            ? proxy + encodeURIComponent(url)
            : proxy + url;

          if (isPost && proxy.includes('allorigins')) {
            clearTimeout(proxyTimeout);
            continue;
          }

          const proxyOpts = {
            method: options.method || 'GET',
            signal: proxyController.signal
          };
          if (isPost) {
            proxyOpts.body = options.body;
            proxyOpts.headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
          }

          const proxyResponse = await fetch(proxiedUrl, proxyOpts);
          clearTimeout(proxyTimeout);

          // OUR server proxy is trusted: return its reply AS-IS (even if it isn't
          // JSON or is an error status) so the caller sees the REAL GPS-server
          // response/diagnostic instead of a vague "CORS" error.
          if (isOwnProxy) {
            const text = await proxyResponse.clone().text().catch(() => '');
            return {
              ok: proxyResponse.ok,
              status: proxyResponse.status,
              text: async () => text,
              json: async () => JSON.parse(text)
            };
          }

          if (proxyResponse.ok) {
            const cloned = proxyResponse.clone();
            const text = await cloned.text();

            if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
              try { sessionStorage.setItem('gpswox_working_proxy', proxy); } catch {}
              return {
                ok: true,
                status: 200,
                text: async () => text,
                json: async () => JSON.parse(text)
              };
            }
            continue;
          }
        } catch {
          continue;
        }
      }

      throw new Error('CORS_OR_NETWORK');
    }
    throw err;
  }
};

export const diagnoseTraccarError = async (response) => {
  const text = await response.text().catch(() => '');
  if (response.status === 401) return { code: 'AUTH', message: 'Email ou mot de passe incorrect' };
  if (response.status === 403) return { code: 'FORBIDDEN', message: 'Accès refusé (403)', details: text.substring(0, 200) };
  if (response.status === 404) return { code: 'NOT_FOUND', message: 'URL incorrecte (404)' };
  if (response.status >= 500) return { code: 'SERVER_ERROR', message: `Erreur serveur (${response.status})` };
  return { code: 'UNKNOWN', message: `Erreur ${response.status}`, details: text.substring(0, 200) };
};

// Per-ACCOUNT session-hash cache key. Using url+identifier means each provider
// account (e.g. main GPSWOX vs Afoulki) keeps its OWN login session and they
// can never reuse each other's hash (which previously returned 0 vehicles).
const hashStoreKey = (url, identifier) =>
  `gpswox_hash::${(url || '').replace(/\/$/, '')}::${identifier || ''}`;

export const traccarLogin = async (url, email, password) => {
  try {
    const baseUrl = (url || '').replace(/\/$/, '');
    const id = (email || '').trim();

    // GPSWOX installs differ on the login field name. Try the common encodings
    // in order so both email-based and username-based accounts authenticate.
    // (Afoulki accounts log in with a USERNAME like "ljscar", not an email.)
    // GPSWOX installs differ on the login field name. Many username-based
    // accounts (like Afoulki "ljscar") still authenticate through the `email`
    // field, so we try every common encoding until one returns a user_api_hash.
    const payloadVariants = id.includes('@')
      ? [
          { email: id, password },
          { email: id, username: id, password },
          { login: id, password },
        ]
      : [
          { email: id, password },                                   // username sent as email (most GPSWOX installs)
          { username: id, password },
          { login: id, password },
          { email: id, username: id, login: id, password },
        ];

    let lastErr = { success: false, error: 'Échec de connexion', errorCode: 'AUTH' };

    // GPSWOX installs expose the login API under different paths. Try each until
    // one responds (a 404 means wrong path → skip straight to the next one).
    const loginPaths = ['/api/login', '/api/authenticate', '/login', '/api/v1/login', '/api/user/login'];

    for (const path of loginPaths) {
    for (const variant of payloadVariants) {
      const formData = new URLSearchParams();
      for (const [k, v] of Object.entries(variant)) formData.append(k, v);

      let response;
      try {
        response = await fetchTraccar(`${baseUrl}${path}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json',
          },
          body: formData.toString(),
        });
      } catch (innerErr) {
        // network/CORS — bubble up to outer catch for a clear message
        throw innerErr;
      }

      if (!response.ok) {
        const diag = await diagnoseTraccarError(response);
        lastErr = { success: false, error: diag.message, errorCode: diag.code, details: diag.details };
        if (response.status === 404) break; // wrong path → stop trying variants, go to next path
        continue;
      }

      let data;
      try {
        const text = await response.text();
        if (!text || text.trim() === '') {
          lastErr = { success: false, error: 'Réponse vide du serveur', errorCode: 'EMPTY_RESPONSE' };
          continue;
        }
        if (!text.trim().startsWith('{') && !text.trim().startsWith('[')) {
          lastErr = { success: false, error: 'Le serveur n\'a pas retourné de JSON valide', errorCode: 'INVALID_JSON', details: text.substring(0, 200) };
          continue;
        }
        data = JSON.parse(text);
      } catch (parseErr) {
        lastErr = { success: false, error: 'Réponse invalide (parsing JSON échoué)', errorCode: 'PARSE_ERROR', details: parseErr.message };
        continue;
      }

      if (data.status === 0 || data.status === '0' || !data.user_api_hash) {
        lastErr = { success: false, error: data.message || 'Identifiant ou mot de passe incorrect', errorCode: 'AUTH' };
        continue;
      }

      try { sessionStorage.setItem(hashStoreKey(url, id), data.user_api_hash); } catch {}
      return {
        success: true,
        user_api_hash: data.user_api_hash,
        user: data.item || data.user || { email: id },
      };
    }
    }

    return lastErr;
  } catch (err) {
    if (err.message === 'CORS_OR_NETWORK') {
      return { 
        success: false, 
        error: 'Erreur réseau ou CORS',
        errorCode: 'CORS',
        details: 'Le serveur GPSWOX n\'autorise pas les connexions depuis ce navigateur. Vérifiez que le serveur a CORS activé.'
      };
    }
    if (err.message === 'TIMEOUT') {
      return { success: false, error: 'Timeout: serveur ne répond pas', errorCode: 'TIMEOUT' };
    }
    return { success: false, error: err.message, errorCode: 'UNKNOWN' };
  }
};

export const traccarLogout = async () => {
  // Clear every per-account session hash.
  try {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && (k === 'gpswox_hash' || k.startsWith('gpswox_hash::'))) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {}
  return { success: true };
};

export const getApiHash = async (settings, returnFullResult = false) => {
  if (!settings || !settings.traccarUrl || !settings.traccarEmail || !settings.traccarPassword) {
    const errResult = { 
      success: false, 
      error: 'Configuration GPS incomplète (URL, email ou mot de passe manquant dans les Paramètres)', 
      errorCode: 'NOT_CONFIGURED' 
    };
    return returnFullResult ? errResult : null;
  }
  try {
    const cached = sessionStorage.getItem(hashStoreKey(settings.traccarUrl, settings.traccarEmail));
    if (cached) {
      return returnFullResult ? { success: true, user_api_hash: cached } : cached;
    }
  } catch {}

  const loginResult = await traccarLogin(settings.traccarUrl, settings.traccarEmail, settings.traccarPassword);
  if (returnFullResult) return loginResult;
  return loginResult.success ? loginResult.user_api_hash : null;
};

export const traccarGetDevices = async (url, email, password) => {
  try {
    const baseUrl = (url || '').replace(/\/$/, '');
    const hash = await getApiHash({ traccarUrl: url, traccarEmail: email, traccarPassword: password });
    if (!hash) return [];
    
    const response = await fetchTraccar(
      `${baseUrl}/api/get_devices?lang=en&user_api_hash=${encodeURIComponent(hash)}`, 
      {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      }
    );
    
    if (!response.ok) {
      if (response.status === 401) {
        try { sessionStorage.removeItem(hashStoreKey(url, email)); } catch {}
      }
      console.error('GPSWOX getDevices failed:', response.status);
      return [];
    }
    
    const data = await response.json();
    let devices = [];
    if (Array.isArray(data)) {
      data.forEach(group => {
        if (group.items && Array.isArray(group.items)) {
          group.items.forEach(d => devices.push(d));
        } else if (group.id) {
          devices.push(group);
        }
      });
    } else if (data.items) {
      devices = data.items;
    }
    
    return devices;
  } catch (err) {
    console.error('GPSWOX getDevices error:', err.message);
    return [];
  }
};

export const traccarGetPositions = async (url, email, password) => {
  const devices = await traccarGetDevices(url, email, password);
  return devices.map(d => ({
    deviceId: d.id,
    latitude: parseFloat(d.lat) || 0,
    longitude: parseFloat(d.lng) || 0,
    speed: parseFloat(d.speed) || 0,
    course: parseFloat(d.course) || 0,
    altitude: parseFloat(d.altitude) || 0,
    address: d.address || '',
    fixTime: d.time || d.timestamp,
    attributes: { device_data: d.device_data || '' }
  }));
};

export const traccarGetDevicePosition = async (url, email, password, deviceId) => {
  const devices = await traccarGetDevices(url, email, password);
  const dev = devices.find(d => String(d.id) === String(deviceId));
  if (!dev) return null;
  return {
    deviceId: dev.id,
    latitude: parseFloat(dev.lat) || 0,
    longitude: parseFloat(dev.lng) || 0,
    speed: parseFloat(dev.speed) || 0,
    course: parseFloat(dev.course) || 0,
    altitude: parseFloat(dev.altitude) || 0,
    address: dev.address || '',
    fixTime: dev.time || dev.timestamp,
    attributes: {}
  };
};

export const traccarSendCommand = async (settings, deviceId, command) => {
  try {
    const baseUrl = (settings.traccarUrl || '').replace(/\/$/, '');
    const loginResult = await getApiHash(settings, true);
    if (!loginResult.success) {
      return { 
        success: false, 
        error: `Auth échouée (${settings.traccarUrl || 'sans URL'}): ${loginResult.error || 'Connexion impossible'}`
      };
    }
    const hash = loginResult.user_api_hash;
    
    console.log('📤 Sending command:', { deviceId, command });
    const isTemplateCmd = String(command).startsWith('template_');
    const cachedEndpoint = sessionStorage.getItem('gpswox_working_cmd_endpoint');
    
    const allAttempts = isTemplateCmd
      ? [
          {
            name: 'send_gprs_command_template',
            url: `${baseUrl}/api/send_gprs_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: command }
          },
          {
            name: 'send_command_template',
            url: `${baseUrl}/api/send_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: command }
          },
          {
            name: 'gprs_send_command_template',
            url: `${baseUrl}/api/gprs_send_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: command }
          },
          {
            name: 'send_gprs_template',
            url: `${baseUrl}/api/send_gprs`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: command }
          },
          {
            name: 'send_command_template_id',
            url: `${baseUrl}/api/send_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), template_id: String(command).replace('template_', '') }
          },
          {
            name: 'gprs_send_command_template_id',
            url: `${baseUrl}/api/gprs_send_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), template_id: String(command).replace('template_', '') }
          },
          {
            name: 'send_gprs_template_id',
            url: `${baseUrl}/api/send_gprs`,
            body: { user_api_hash: hash, device_id: String(deviceId), template_id: String(command).replace('template_', '') }
          },
          {
            name: 'device_command_template',
            url: `${baseUrl}/api/device_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: command }
          }
        ]
      : [
          {
            name: 'send_gprs_command_custom',
            url: `${baseUrl}/api/send_gprs_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: 'custom', data: command }
          },
          {
            name: 'send_gprs_command_gprs',
            url: `${baseUrl}/api/send_gprs_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: 'gprs', data: command }
          },
          {
            name: 'gprs_send_command_data',
            url: `${baseUrl}/api/gprs_send_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), data: command, type: 'gprs' }
          },
          {
            name: 'send_gprs',
            url: `${baseUrl}/api/send_gprs`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: 'gprs', data: command }
          },
          {
            name: 'gprs_send_command_msg',
            url: `${baseUrl}/api/gprs_send_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), message: command, type: 'gprs' }
          },
          {
            name: 'send_command',
            url: `${baseUrl}/api/send_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), type: 'gprs', gprs: command }
          },
          {
            name: 'device_command',
            url: `${baseUrl}/api/device_command`,
            body: { user_api_hash: hash, device_id: String(deviceId), command: command, type: 'gprs' }
          }
        ];
    
    let attempts = allAttempts;
    if (cachedEndpoint) {
      const cached = allAttempts.find(a => a.name === cachedEndpoint);
      const others = allAttempts.filter(a => a.name !== cachedEndpoint);
      if (cached) {
        attempts = [cached, ...others];
      }
    }
    
    let lastError = null;
    let lastDetails = null;
    
    for (const attempt of attempts) {
      try {
        const formData = new URLSearchParams();
        for (const [k, v] of Object.entries(attempt.body)) {
          formData.append(k, v);
        }
        
        const response = await fetchTraccar(attempt.url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          body: formData.toString()
        });
        
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          if (data.status === 0 || data.status === '0' || data.error) {
            lastError = data.message || data.error || 'Échec';
            lastDetails = JSON.stringify(data).substring(0, 200);
            continue;
          }
          try {
            sessionStorage.setItem('gpswox_working_cmd_endpoint', attempt.name);
          } catch {}
          return { success: true, data, endpoint: attempt.url };
        } else {
          const text = await response.text().catch(() => '');
          lastError = `HTTP ${response.status}`;
          lastDetails = text.substring(0, 200);
        }
      } catch (e) {
        lastError = e.message;
        if (e.message === 'TIMEOUT') {
          return { 
            success: false, 
            error: 'Le serveur Athuraya ne répond pas (timeout). Réessayez dans quelques secondes.',
            errorCode: 'TIMEOUT'
          };
        }
      }
    }
    
    return { 
      success: false, 
      error: lastError || 'Aucun endpoint ne répond',
      details: lastDetails,
      errorCode: 'ALL_FAILED'
    };
  } catch (err) {
    if (err.message === 'CORS_OR_NETWORK') {
      return { success: false, error: 'Erreur réseau ou CORS', errorCode: 'CORS' };
    }
    return { success: false, error: err.message, errorCode: 'UNKNOWN' };
  }
};

export const traccarGetTemplates = async (settings, deviceId) => {
  try {
    const baseUrl = (settings.traccarUrl || '').replace(/\/$/, '');
    const hash = await getApiHash(settings);
    if (!hash) {
      return [];
    }
    
    const cachedEndpoint = sessionStorage.getItem('gpswox_working_tmpl_endpoint');
    const allEndpoints = [
      { name: 'gprs_templates_dev', url: `${baseUrl}/api/get_gprs_templates?user_api_hash=${encodeURIComponent(hash)}&device_id=${deviceId}` },
      { name: 'gprs_templates', url: `${baseUrl}/api/get_gprs_templates?user_api_hash=${encodeURIComponent(hash)}` },
      { name: 'commands_dev', url: `${baseUrl}/api/get_commands?user_api_hash=${encodeURIComponent(hash)}&device_id=${deviceId}` },
      { name: 'commands', url: `${baseUrl}/api/get_commands?user_api_hash=${encodeURIComponent(hash)}` },
      { name: 'device_commands', url: `${baseUrl}/api/get_device_commands?user_api_hash=${encodeURIComponent(hash)}&device_id=${deviceId}` },
      { name: 'templates', url: `${baseUrl}/api/get_templates?user_api_hash=${encodeURIComponent(hash)}&device_id=${deviceId}` },
    ];
    
    let endpoints = allEndpoints;
    if (cachedEndpoint) {
      const cached = allEndpoints.find(e => e.name === cachedEndpoint);
      const others = allEndpoints.filter(e => e.name !== cachedEndpoint);
      if (cached) endpoints = [cached, ...others];
    }
    
    for (const ep of endpoints) {
      try {
        const response = await fetchTraccar(ep.url, {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        if (!response.ok) {
          continue;
        }
        
        const data = await response.json();
        let items = [];
        if (Array.isArray(data)) items = data;
        else if (data.items && Array.isArray(data.items)) items = data.items;
        else if (data.data && Array.isArray(data.data)) items = data.data;
        else if (data.templates && Array.isArray(data.templates)) items = data.templates;
        else if (data.commands && Array.isArray(data.commands)) items = data.commands;
        
        if (items.length > 0) {
          try { sessionStorage.setItem('gpswox_working_tmpl_endpoint', ep.name); } catch {}
          return items;
        }
      } catch (e) {
        if (e.message === 'TIMEOUT') {
          return [];
        }
        continue;
      }
    }
    
    return [];
  } catch (err) {
    return [];
  }
};

export const traccarFetchAll = async (settings) => {
  if (!settings.traccarEnabled || !settings.traccarUrl || !settings.traccarEmail || !settings.traccarPassword) {
    return { success: false, error: 'GPS non configuré' };
  }

  const { traccarUrl, traccarEmail, traccarPassword } = settings;

  // Log in FIRST so we can report the real cause (auth / CORS / server) instead
  // of a vague "no devices" — this is what the Afoulki test button shows.
  const login = await getApiHash(
    { traccarUrl, traccarEmail, traccarPassword },
    true
  );
  if (!login || !login.success) {
    return {
      success: false,
      error: (login && login.error) || 'Connexion impossible',
      errorCode: (login && login.errorCode) || 'AUTH',
    };
  }

  const devices = await traccarGetDevices(traccarUrl, traccarEmail, traccarPassword);

  if (devices.length === 0) {
    return { success: false, error: 'Connexion OK mais aucun véhicule sur ce compte', errorCode: 'NO_DEVICES' };
  }
  
  const extractOdometer = (device) => {
    const sensors = device.sensors || [];
    const odoSensor = sensors.find(s => {
      const name = (s.name || '').toLowerCase();
      const type = (s.type || '').toLowerCase();
      return type === 'odometer' || 
             name.includes('odometer') || 
             name.includes('mileage') ||
             name.includes('km total') ||
             name.includes('kilometer');
    });
    
    if (odoSensor) {
      const val = parseFloat(odoSensor.value || odoSensor.val || 0);
      const unit = (odoSensor.unit_of_measurement || odoSensor.unit || 'km').toLowerCase();
      if (unit.includes('m') && !unit.includes('km') && !unit.includes('mi')) {
        return val / 1000;
      }
      if (unit.includes('mi')) {
        return val * 1.60934;
      }
      return val;
    }
    
    if (device.odometer !== undefined) return parseFloat(device.odometer);
    if (device.distance !== undefined) return parseFloat(device.distance);
    if (device.total_distance !== undefined) return parseFloat(device.total_distance) / 1000;
    
    return null;
  };
  
  const result = devices.map(device => ({
    id: device.id,
    name: device.name || `Device ${device.id}`,
    uniqueId: device.imei || device.unique_id || String(device.id),
    // GPSWOX statuses: 'online' (moving/connected), 'ack' (connected but engine
    // OFF / stopped — STILL online), 'offline' (not communicating). A stopped
    // vehicle reports 'ack' and must NOT be shown as disconnected.
    status: (device.online === 'offline' || device.online === 0 || device.online === false)
      ? 'offline' : 'online',
    lastUpdate: device.time || device.timestamp,
    odometer: extractOdometer(device),
    sensors: device.sensors || [],
    raw: device, // full provider object (for DI/hood sensor detection)
    position: (device.lat && device.lng) ? {
      latitude: parseFloat(device.lat),
      longitude: parseFloat(device.lng),
      speed: parseFloat(device.speed) || 0,
      course: parseFloat(device.course) || 0,
      altitude: parseFloat(device.altitude) || 0,
      address: device.address || '',
      fixTime: device.time || device.timestamp,
      attributes: { protocol: device.protocol || '' }
    } : null
  }));
  
  return { success: true, data: result };
};
